/**
 * CTA 追踪工具
 * 用于记录用户从哪个 CTA 进入 Contact Us 或 Chat With Us
 *
 * 核心功能：
 * 1. intent_id: 每次 CTA 点击生成唯一 ID，用于关联后续提交事件
 * 2. generate_lead: CTA 点击时触发，每个 session 只触发一次
 * 3. CTA 信息存储: 用于后续事件的参数传递
 */

import { pushToDataLayer } from './gtm';
import { getUtmParams, getFirstPageUrl, getLandingPage } from './utmParams';

export interface CTAInfo {
  lead_channel: 'form' | 'chat';
  cta_location: 'header' | 'footer' | 'contact_page' | 'Q&A' | 'floating_widget';
  cta_type: 'button' | 'link' | 'widget' | 'direct';
  cta_id: string;
}

// Storage Keys
const CTA_STORAGE_KEY = 'cta_tracking_info';
// 双归因 Storage Keys
const FIRST_INTENT_ID_KEY = 'first_intent_id';
const FIRST_CTA_LOCATION_KEY = 'first_cta_location';
const LAST_INTENT_ID_KEY = 'last_intent_id';
const LAST_CTA_LOCATION_KEY = 'last_cta_location';
const INTENT_TIME_KEY = 'last_intent_time';
const INTENT_CTA_TYPE_KEY = 'last_cta_type';
const LEAD_CLICK_KEY = 'intent_event_fired';

/**
 * 生成 intent_id（UUID）并实现双归因存储
 * - first_intent_id: 首次 CTA 点击时写入，后续不变
 * - last_intent_id: 每次 CTA 点击都覆盖
 */
export function generateIntentId(ctaLocation: string): string {
  const intentId = crypto.randomUUID();
  const eventTime = new Date().toISOString();

  // First-Touch 逻辑：只在首次点击时写入
  if (!sessionStorage.getItem(FIRST_INTENT_ID_KEY)) {
    sessionStorage.setItem(FIRST_INTENT_ID_KEY, intentId);
    sessionStorage.setItem(FIRST_CTA_LOCATION_KEY, ctaLocation);
    console.log('[Intent] First touch recorded:', { intentId, ctaLocation });
  }

  // Last-Touch 逻辑：每次都覆盖
  sessionStorage.setItem(LAST_INTENT_ID_KEY, intentId);
  sessionStorage.setItem(LAST_CTA_LOCATION_KEY, ctaLocation);
  sessionStorage.setItem(INTENT_TIME_KEY, eventTime);

  console.log('[Intent] Last touch updated:', { intentId, ctaLocation });
  return intentId;
}

/**
 * 获取首次 intent_id（First-Touch）
 */
export function getFirstIntentId(): string | null {
  return sessionStorage.getItem(FIRST_INTENT_ID_KEY);
}

/**
 * 获取首次 CTA 位置（First-Touch）
 */
export function getFirstCtaLocation(): string | null {
  return sessionStorage.getItem(FIRST_CTA_LOCATION_KEY);
}

/**
 * 获取最后 intent_id（Last-Touch）
 */
export function getLastIntentId(): string | null {
  return sessionStorage.getItem(LAST_INTENT_ID_KEY);
}

/**
 * 获取最后 CTA 位置（Last-Touch）
 */
export function getLastCtaLocation(): string | null {
  return sessionStorage.getItem(LAST_CTA_LOCATION_KEY);
}

/**
 * 获取最近一次的 intent 时间
 */
export function getLastIntentTime(): string | null {
  return sessionStorage.getItem(INTENT_TIME_KEY);
}

/**
 * 检查 generate_lead 是否已在当前 session 触发过
 */
export function hasGenerateLeadFired(): boolean {
  return sessionStorage.getItem(LEAD_CLICK_KEY) === 'true';
}

/**
 * 标记 generate_lead 已触发
 */
export function markGenerateLeadFired(): void {
  sessionStorage.setItem(LEAD_CLICK_KEY, 'true');
}

/**
 * 保存 CTA 信息到 sessionStorage，并触发 generate_lead 事件
 *
 * 双归因机制：
 * - first_intent_id / first_cta_location: 首次 CTA 点击时写入，后续不变
 * - last_intent_id / last_cta_location: 每次 CTA 点击都覆盖
 *
 * generate_lead 事件每个 session 只触发一次
 */
export function saveCTAInfo(info: CTAInfo): void {
  try {
    // 保存 CTA 信息
    sessionStorage.setItem(CTA_STORAGE_KEY, JSON.stringify(info));

    // 保存 CTA 类型（用于提交事件读取）
    sessionStorage.setItem(INTENT_CTA_TYPE_KEY, info.cta_type);

    // 生成 intent_id 并实现双归因存储
    const intentId = generateIntentId(info.cta_location);
    const eventTime = new Date().toISOString();

    // 获取 UTM 参数
    const utmParams = getUtmParams();

    // 触发 generate_lead 事件（每个 session 只触发一次）
    if (!hasGenerateLeadFired()) {
      pushToDataLayer('generate_lead', {
        // Intent 标识
        intent_id: intentId,
        // CTA 上下文
        cta_id: info.cta_id,
        lead_channel: info.lead_channel,
        cta_location: info.cta_location,
        cta_type: info.cta_type,
        event_time: eventTime,
        // 页面上下文
        user_agent: navigator.userAgent,
        first_page_url: getFirstPageUrl(),
        landing_page: getLandingPage(),
        // UTM 参数（从 storage 读取，不生成）
        ...utmParams,
      });

      markGenerateLeadFired();
    }
  } catch (error) {
    console.error('[CTA Tracking] Failed to save:', error);
  }
}

/**
 * 从 sessionStorage 获取 CTA 信息
 */
export function getCTAInfo(): CTAInfo | null {
  try {
    const stored = sessionStorage.getItem(CTA_STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored) as CTAInfo;
    }
  } catch (error) {
    console.error('[CTA Tracking] Failed to get:', error);
  }
  return null;
}

/**
 * 清除 CTA 信息
 */
export function clearCTAInfo(): void {
  try {
    sessionStorage.removeItem(CTA_STORAGE_KEY);
    console.log('[CTA Tracking] Cleared');
  } catch (error) {
    console.error('[CTA Tracking] Failed to clear:', error);
  }
}

/**
 * 获取 CTA 信息，如果没有则返回默认值（直接访问）
 */
export function getCTAInfoWithDefault(leadChannel: 'form' | 'chat' = 'form'): CTAInfo {
  const info = getCTAInfo();
  if (info) {
    return info;
  }
  // 默认值：直接访问页面，没有经过 CTA
  return {
    lead_channel: leadChannel,
    cta_location: 'contact_page',
    cta_type: 'direct',
    cta_id: 'direct-access'
  };
}

// 预定义的 CTA 配置，方便调用
export const CTA_CONFIG = {
  // Header Contact Us 链接
  HEADER_CONTACT: {
    lead_channel: 'form' as const,
    cta_location: 'header' as const,
    cta_type: 'link' as const,
    cta_id: 'cta-contact-header'
  },
  // Footer Contact Us 菜单链接
  FOOTER_CONTACT: {
    lead_channel: 'form' as const,
    cta_location: 'footer' as const,
    cta_type: 'link' as const,
    cta_id: 'cta-contact-footer'
  },
  // Footer Inquiry 按钮
  FOOTER_INQUIRY: {
    lead_channel: 'form' as const,
    cta_location: 'footer' as const,
    cta_type: 'button' as const,
    cta_id: 'cta-inquiry-footer'
  },
  // Q&A 中的 Contact us 链接
  QA_CONTACT: {
    lead_channel: 'form' as const,
    cta_location: 'Q&A' as const,
    cta_type: 'link' as const,
    cta_id: 'cta-qa-contactus'
  },
  // Chat with us 浮动按钮
  CHAT_WIDGET: {
    lead_channel: 'chat' as const,
    cta_location: 'floating_widget' as const,
    cta_type: 'widget' as const,
    cta_id: 'cta-chat-widget'
  }
};

